import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import PatientDetailsStep from "./PatientDetailsStep";
import OrderDetailsStep from "./OrderDetailsStep";
import type { Step1Data } from "./types";

interface CreateNewOrderProps {
  onBack: () => void;
}

const CreateNewOrder: React.FC<CreateNewOrderProps> = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);

  const handleStep1Complete = (data: Step1Data) => {
    setStep1Data(data);
    setCurrentStep(2);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-5">
        {/* Back to Order Management */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700"
        >
          <ChevronLeft size={16} /> Back to Orders
        </button>

        {/* Step Progress Indicator */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                currentStep >= 1
                  ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              1
            </div>
            <span className={`text-xs font-bold ${currentStep >= 1 ? "text-emerald-600" : "text-slate-400"}`}>
              Patient Details
            </span>
          </div>
          <div className={`flex-1 h-[2px] max-w-[120px] rounded-full transition-all ${currentStep >= 2 ? "bg-emerald-500" : "bg-slate-200"}`} />
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                currentStep >= 2
                  ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              2
            </div>
            <span className={`text-xs font-bold ${currentStep >= 2 ? "text-emerald-600" : "text-slate-400"}`}>
              Order Details
            </span>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <PatientDetailsStep
            initialData={step1Data}
            onNext={handleStep1Complete}
          />
        )}

        {currentStep === 2 && step1Data && (
          <OrderDetailsStep
            step1Data={step1Data}
            onBack={handleBackToStep1}
            onOrderCreated={onBack}
          />
        )}
      </div>
    </div>
  );
};

export default CreateNewOrder;
